<!-- Fixed back bar - always visible -->
<div class="back-bar">
  <button type="button" class="back-btn" (click)="goBack()"
    [attr.aria-label]="(comingFromFinishOrder ? 'HOME.ALL_MAGNETS.BACK_TO_ORDER' : 'HOME.ALL_MAGNETS.BACK') | translate">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
    <span>{{ (comingFromFinishOrder ? 'HOME.ALL_MAGNETS.BACK_TO_ORDER' : 'HOME.ALL_MAGNETS.BACK') | translate }}</span>
  </button>
</div>

<div class="all-magnets-page">
  <header class="page-header">
    <div class="section-header">
      <h1>{{ 'HOME.ALL_MAGNETS.TITLE' | translate }}</h1>
      <p class="section-subtitle">{{ 'HOME.ALL_MAGNETS.SUBTITLE' | translate }}</p>
    </div>
  </header>

  <div class="search-container">
    <div class="search-input-wrapper">
      <img loading="lazy" src="../../../assets/images/search.svg" alt="Search" class="search-icon">
      <input
        type="text"
        class="search-input"
        [(ngModel)]="searchQuery"
        (input)="onSearchChange()"
        [placeholder]="'HOME.SHOPIFY_PRODUCTS.SEARCH_PLACEHOLDER' | translate">
    </div>
  </div>

  @if (isLoading) {
    <div class="loading-container">
      <div class="products-grid">
        @for (i of [1,2,3,4,5,6,7,8,9,10]; track i) {
          <div class="product-card skeleton">
            <div class="skeleton-image"></div>
            <div class="skeleton-title"></div>
            <div class="skeleton-price"></div>
            <div class="skeleton-button"></div>
          </div>
        }
      </div>
    </div>
  }

  @if (loadError && !isLoading) {
    <div class="error-container">
      <p>{{ 'HOME.SHOPIFY_PRODUCTS.LOAD_ERROR' | translate }}</p>
    </div>
  }

  @if (!isLoading && !loadError) {
    <div class="products-container">
      @if (filteredProducts.length === 0) {
        <div class="no-results">
          <p>{{ 'HOME.SHOPIFY_PRODUCTS.NO_RESULTS' | translate }}</p>
        </div>
      }
      @if (filteredProducts.length > 0) {
        <div class="products-grid">
          @for (product of filteredProducts; track product) {
            <div class="product-card">
              <div class="product-image-container">
                <div class="image-loader"></div>
                <img [src]="product.imageUrl" [alt]="product.title" class="product-image" loading="lazy" (load)="onImageLoad($event)" (error)="onImageError($event)">
                @if (hasDiscount(product)) {
                  <div class="discount-badge">
                    -{{ getDiscountPercentage(product) }}%
                  </div>
                }
              </div>
              <div class="product-info">
                <h3 class="product-title">{{ product.title }}</h3>
                <div class="product-pricing">
                  <span class="product-price">{{ formatPrice(product.price.amount, product.price.currencyCode) }}</span>
                  @if (hasDiscount(product)) {
                    <span class="product-compare-price">
                      {{ formatPrice(product.compareAtPrice!.amount, product.compareAtPrice!.currencyCode) }}
                    </span>
                  }
                </div>
                <button class="add-to-cart-btn" (click)="addToCart(product)">
                  {{ 'HOME.SHOPIFY_PRODUCTS.ADD_TO_CART' | translate }}
                </button>
              </div>
            </div>
          }
        </div>
      }
    </div>
  }

  <div class="cart-notification" [class.show]="showAddedToCartNotification">
    <div class="cart-notification-content">
      <img loading="lazy" src="../../../assets/images/CheckCircle.svg" alt="Success" class="notification-icon">
      <div class="notification-text">
        <strong>{{ addedProductName }}</strong>
        <span>{{ 'HOME.SHOPIFY_PRODUCTS.ADDED_TO_CART' | translate }}</span>
      </div>
    </div>
  </div>

  <!-- Floating Cart Button (same as home – shown when cart has items) -->
  @if (isMainStore && totalCartItems > 0) {
    <button
      class="floating-cart-btn"
      (click)="openCart()">
      <img loading="lazy" src="../../../assets/images/cart.svg" alt="Cart" class="cart-icon">
      <span class="cart-count">{{ totalCartItems }}</span>
    </button>
  }
</div>

<!-- Cart Modal (in-place on All Magnets – no navigation) -->
@if (showCartModal) {
  <div class="cart-modal-backdrop" (click)="closeCartModal()"></div>
}
<div class="cart-modal" [class.cart-modal--open]="showCartModal" role="dialog" aria-modal="true">
  <div class="cart-modal-header">
    <h3 class="cart-modal-title">{{ 'HOME.YOUR_CART' | translate }}</h3>
    <button class="cart-modal-close" (click)="closeCartModal()" aria-label="Close">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </button>
  </div>

  <div class="cart-modal-body">
    @if (totalCartItems === 0) {
      <div class="cart-modal-empty">
        <img loading="lazy" src="../../../assets/images/cart.svg" alt="Cart" class="cart-modal-empty-icon">
        <p>{{ 'HOME.CART_EMPTY' | translate }}</p>
      </div>
    }

    @if (designQueue.length > 0) {
      <div class="cart-modal-section">
        <h4 class="cart-modal-section-title">{{ 'HOME.CUSTOM_MAGNETS' | translate }}</h4>
        @for (design of designQueue; track design; let i = $index) {
          <div class="cart-modal-item">
            <div class="cart-modal-item-thumb">
              <img [src]="design.thumbnailUrl" alt="Design {{i + 1}}">
            </div>
            <div class="cart-modal-item-info">
              <span class="cart-modal-item-name">{{ 'HOME.DESIGN' | translate }} #{{i + 1}}</span>
              <span class="cart-modal-item-price">{{ formatCartPrice(design.quantity * customMagnetUnitPrice) }}</span>
            </div>
            <div class="cart-modal-item-qty">
              <button (click)="onUpdateDesignQuantity({id: design.id, quantity: design.quantity - 1})" [disabled]="design.quantity <= 1">
                <img loading="lazy" width="16" height="16" src="../../../assets/images/Minus.svg" alt="-">
              </button>
              <span>{{ design.quantity }}</span>
              <button (click)="onUpdateDesignQuantity({id: design.id, quantity: design.quantity + 1})">
                <img loading="lazy" width="16" height="16" src="../../../assets/images/Plus.svg" alt="+">
              </button>
            </div>
          </div>
        }
      </div>
    }

    @if (cartShopifyProducts.length > 0) {
      <div class="cart-modal-section">
        <h4 class="cart-modal-section-title">{{ 'HOME.SHOPIFY_PRODUCTS_CART' | translate }}</h4>
        @for (item of cartShopifyProducts; track item) {
          <div class="cart-modal-item">
            <div class="cart-modal-item-thumb">
              <img [src]="item.product.imageUrl" [alt]="item.product.title">
            </div>
            <div class="cart-modal-item-info">
              <span class="cart-modal-item-name">{{ item.product.title }}</span>
              <span class="cart-modal-item-price">{{ formatCartPrice(+item.product.price.amount * item.quantity) }}</span>
            </div>
            <div class="cart-modal-item-qty">
              <button (click)="updateCartShopifyQuantity(item.product.id, item.quantity - 1)" [disabled]="item.quantity <= 1">
                <img loading="lazy" width="16" height="16" src="../../../assets/images/Minus.svg" alt="-">
              </button>
              <span>{{ item.quantity }}</span>
              <button (click)="updateCartShopifyQuantity(item.product.id, item.quantity + 1)">
                <img loading="lazy" width="16" height="16" src="../../../assets/images/Plus.svg" alt="+">
              </button>
            </div>
            <button class="cart-modal-item-remove" (click)="removeCartShopifyProduct(item.product.id)" aria-label="Remove">
              <img loading="lazy" width="24" height="24" src="../../../assets/images/delete.svg" alt="Remove">
            </button>
          </div>
        }
      </div>
    }
  </div>

  @if (totalCartItems > 0) {
    <div class="cart-modal-footer">
      <div class="cart-modal-total">
        <span class="cart-modal-total-label">{{ 'HOME.TOTAL' | translate }}</span>
        <span class="cart-modal-total-amount">{{ formatCartPrice(cartModalTotal) }}</span>
      </div>
      <button class="cart-modal-checkout-btn" (click)="checkoutFromModal()">
        {{ 'HOME.BUY_NOW' | translate }}
      </button>
      <button class="cart-modal-continue-btn" (click)="closeCartModal()">
        {{ 'HOME.CONTINUE_SHOPPING' | translate }}
      </button>
    </div>
  }
</div>
